/**
 * Every edit the GUI can make, as plain functions.
 *
 * Windows call these instead of building requests themselves. Each function sends one request;
 * when the core refuses, the reason it gives ("circular inheritance: a -> b -> a") is shown as
 * an error toast and the function resolves with `null`, so callers rarely need `try`.
 *
 * Nothing here changes the local graph: the core answers with a `graph.changed` event that the
 * mirror applies (see `graph.ts`).
 */
import type { GraphChange } from "@protocol/GraphChange";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { GroupPatch } from "@protocol/GroupPatch";
import type { Level } from "@protocol/Level";
import type { Link } from "@protocol/Link";
import type { LinkKind } from "@protocol/LinkKind";
import type { LinkPatch } from "@protocol/LinkPatch";
import type { NodeKind } from "@protocol/NodeKind";
import type { NewLink } from "@protocol/NewLink";
import type { NodePatch } from "@protocol/NodePatch";
import type { Node } from "@protocol/Node";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Pin } from "@protocol/Pin";
import type { Request } from "@protocol/Request";
import type { Response } from "@protocol/Response";
import { toast } from "../shared/toast";
import type { CommandSet } from "../shared/commands";
import type { MenuSection, ToolbarButton } from "../shared/menus";
import type { GraphMirror } from "./graph";
import { isClientError, type Client } from "./client";

/** What is needed to create a node. The core derives the id from the name unless one is given. */
export interface NewNode {
  /** Its id, chosen by the page (a paste that links its nodes to each other); none: from the name. */
  id?: string;
  name: string;
  kind: NodeKind;
  /** The categories it is in (names; a new name makes a new category). */
  categories: string[];
  note?: string;
  /** The values of its environment's fields (a language, code), by field id. */
  data?: { [key in string]: string };
  /** The project's assets attached to it (paths). */
  attachments?: string[];
  /** Where it sits on a board. */
  board?: BoardPlace;
  /** Its look on a board (fill, colour, font size, shape...). */
  style?: { [key in string]: string };
  /** The group (a board's frame) it goes in. */
  group?: string;
  /** Its ways out (a blueprint's pins). */
  pins?: Pin[];
  /** Where its box sits in the class diagram (top-left corner). */
  diagram?: [number, number];
}

/** A node created by {@link Actions.addNode}: its id, and the links the core skipped. */
export interface Added {
  id: string;
  /** Why some requested links were not created (target missing, rule broken...). */
  warnings: string[];
}

/**
 * A whole node from what its creator chose (the core gives it its id); `place` stamps it with the
 * environment and board worked in (`null`: {@link placedEdits} does it for a batch).
 */
function fullNode(node: NewNode, place: Placement | null): Node {
  return {
    id: node.id ?? "",
    name: node.name,
    kind: node.kind,
    categories: node.categories,
    note: node.note ?? "",
    ...(Object.values(node.data ?? {}).some((v) => v) ? { data: Object.fromEntries(Object.entries(node.data ?? {}).filter(([, v]) => v)) as { [key in string]: string } } : {}),
    comments: [],
    group: node.group ?? null,
    ...(node.attachments?.length ? { attachments: node.attachments } : {}),
    ...(node.board ? { board: node.board } : {}),
    ...(node.style && Object.keys(node.style).length ? { style: node.style } : {}),
    ...(node.pins?.length ? { pins: node.pins } : {}),
    ...(node.diagram ? { diagram: node.diagram } : {}),
    ...(place ? placedIn(place) : {}),
  } as Node;
}

/** The edit functions. Every one resolves with the core's changes, or `null` if refused. */
export interface Actions {
  /**
   * Creates a node, and links from it, in one undoable edit; `first` (a `define_kinds` edit
   * for a plugin's kind the graph lacks, the diagram's boxes fixed where they are drawn) goes
   * first in the same edit.
   */
  addNode(node: NewNode, links?: NewLink[], first?: GraphEdit[]): Promise<Added | null>;
  /**
   * Creates several nodes in one undoable edit (a paste, a duplicate), then `links` between
   * them, with `first` before; resolves with their ids, in order.
   */
  addNodes(nodes: NewNode[], links?: Link[], first?: GraphEdit[]): Promise<string[] | null>;
  updateNode(id: string, patch: NodePatch): Promise<GraphChange[] | null>;
  removeNode(id: string): Promise<GraphChange[] | null>;
  /** Removes several nodes and their links in one undoable edit. */
  removeNodes(ids: string[]): Promise<GraphChange[] | null>;
  /** Links two nodes; `pin`: the pin of `from` it leaves from (a blueprint wire). */
  addLink(from: string, kind: LinkKind, to: string, label?: string, pin?: string): Promise<GraphChange[] | null>;
  removeLink(link: Link): Promise<GraphChange[] | null>;
  updateLink(link: Link, patch: LinkPatch): Promise<GraphChange[] | null>;
  addComment(id: string, text: string): Promise<GraphChange[] | null>;
  removeComment(id: string, index: number): Promise<GraphChange[] | null>;
  /** Creates a group (inside `parent`, if given) holding `members`, in one undoable edit. */
  addGroup(name: string, parent?: string | null, members?: string[]): Promise<GraphChange[] | null>;
  /** Renames, re-parents (`parent: ""` = top level) or describes a group. */
  updateGroup(id: string, patch: GroupPatch): Promise<GraphChange[] | null>;
  /** Removes a group; its nodes and sub-groups move to its parent. */
  removeGroup(id: string): Promise<GraphChange[] | null>;
  /** Several edits (notes, comments, groups) in one undoable step; all or nothing. */
  batch(edits: GraphEdit[]): Promise<GraphChange[] | null>;
  /** Adds workspaces named `names` to `environment`, in order, in one undoable step (an environment's first holds what it already has). */
  addWorkspaces(environment: string, names: string[]): Promise<GraphChange[] | null>;
  /** Renames a workspace. */
  renameWorkspace(id: string, name: string): Promise<GraphChange[] | null>;
  /** Removes a workspace and everything on it, in one undoable step. */
  removeWorkspace(id: string): Promise<GraphChange[] | null>;
  /** Undoes this person's last edit (everyone has their own history). */
  undo(): Promise<GraphChange[] | null>;
  /** Redoes this person's last undone edit. */
  redo(): Promise<GraphChange[] | null>;
  /** Writes an entry in the core's log, tagged `ui:<source>`. Never fails. */
  log(source: string, level: Level, message: string): void;
}

/**
 * Creates the edit functions on top of `client`. `historyRefused` runs when the core refused
 * an undo or redo: it then took that edit off this person's history without any
 * `graph.changed`, so whether undo is still possible must be asked again.
 */
/** Where the page adds things: its environment and the workspace of it worked in. */
export interface Placement {
  /** The environment (`null`: the project has none). */
  environment: string | null;
  /** The workspace (`null`: the environment is one board). */
  workspace: string | null;
}

/** The fields a new node takes from `place`. */
function placedIn(place: Placement): { environment?: string; workspace?: string } {
  if (!place.environment) return {};
  return { environment: place.environment, ...(place.workspace ? { workspace: place.workspace } : {}) };
}

/** The edits of a batch, with the nodes they add placed in `place` when they say no environment (groups follow their members in the core). */
export function placedEdits(edits: GraphEdit[], place: Placement): GraphEdit[] {
  if (!place.environment) return edits;
  return edits.map((e) =>
    e.edit === "add_node" && !e.node.environment
      ? { ...e, node: { ...e.node, ...placedIn(place) } }
      : // A group made empty (a frame drawn on a board) has no member to follow.
        e.edit === "add_group" && !e.members.length && !e.parent && !e.environment
        ? { ...e, ...placedIn(place) }
        : e,
  );
}

/**
 * Creates the actions. `place` is the environment and workspace the page works in
 * (`state/environments.ts` through the graph mirror's scope): the nodes it adds go there.
 */
export function createActions(client: Client, historyRefused: () => void = () => {}, place: () => Placement = () => ({ environment: null, workspace: null })): Actions {
  /** Sends a request; on refusal shows the core's reason and returns `null`. */
  async function send(request: Request): Promise<Response | null> {
    try {
      return await client.request(request);
    } catch (error) {
      toast(isClientError(error) ? error.message : String(error), "error");
      return null;
    }
  }

  /** Sends an edit and returns the changes it made. */
  async function edit(request: Request): Promise<GraphChange[] | null> {
    const response = await send(request);
    return response?.type === "graph_updated" ? response.changes : null;
  }

  /** Sends an undo or redo; after a refusal, has the undo state asked again. */
  async function history(request: Request): Promise<GraphChange[] | null> {
    const changes = await edit(request);
    if (!changes) historyRefused();
    return changes;
  }

  return {
    async addNodes(nodes, links = [], first = []) {
      const added: GraphEdit[] = nodes.map((node) => ({ edit: "add_node", node: fullNode(node, null), links: [] }));
      const linked: GraphEdit[] = links.map((link) => ({ edit: "add_link", link }));
      const changes = await edit({ method: "graph.batch", params: { edits: [...first, ...placedEdits(added, place()), ...linked] } });
      return changes ? changes.flatMap((c) => (c.type === "node_added" ? [c.node.id] : [])) : null;
    },
    async addNode(node, links = [], first = []) {
      const full = fullNode(node, place());
      const response = await send(
        first.length
          ? { method: "graph.batch", params: { edits: [...first, { edit: "add_node", node: full, links }] } }
          : { method: "graph.add_node", params: { links, node: full } },
      );
      if (response?.type !== "graph_updated") return null;
      const added = response.changes.find((c) => c.type === "node_added");
      return added?.type === "node_added" ? { id: added.node.id, warnings: response.warnings } : null;
    },
    updateNode: (id, patch) => edit({ method: "graph.update_node", params: { id, patch } }),
    removeNode: (id) => edit({ method: "graph.remove_node", params: { id } }),
    removeNodes: (ids) => edit({ method: "graph.remove_nodes", params: { ids } }),
    addLink: (from, kind, to, label = "", pin) => edit({ method: "graph.add_link", params: { link: { from, to, kind, label, ...(pin ? { pin } : {}) } } }),
    removeLink: (link) => edit({ method: "graph.remove_link", params: { link } }),
    updateLink: (link, patch) => edit({ method: "graph.update_link", params: { link, patch } }),
    addComment: (id, text) => edit({ method: "graph.add_comment", params: { id, text } }),
    removeComment: (id, index) => edit({ method: "graph.remove_comment", params: { id, index } }),
    addGroup: (name, parent = null, members = []) => edit({ method: "graph.add_group", params: { name, parent, members } }),
    updateGroup: (id, patch) => edit({ method: "graph.update_group", params: { id, patch } }),
    removeGroup: (id) => edit({ method: "graph.remove_group", params: { id } }),
    batch: (edits) => edit({ method: "graph.batch", params: { edits: placedEdits(edits, place()) } }),
    addWorkspaces: (environment, names) => edit({ method: "graph.batch", params: { edits: names.map((name) => ({ edit: "add_workspace", name, environment })) } }),
    renameWorkspace: (id, name) => edit({ method: "graph.update_workspace", params: { id, name } }),
    removeWorkspace: (id) => edit({ method: "graph.remove_workspace", params: { id } }),
    undo: () => history({ method: "graph.undo" }),
    redo: () => history({ method: "graph.redo" }),
    log(source, level, message) {
      client.request({ method: "log.submit", params: { level, source: `ui:${source}`, message } }).catch(() => {});
    },
  };
}

/** The graph's shortcut commands: undo and redo. */
export function graphCommands(actions: Actions): CommandSet {
  return {
    "graph.undo": () => void actions.undo(),
    "graph.redo": () => void actions.redo(),
  };
}

/** The edits' menu lines: undo and redo (Edit). */
export function graphMenus(graph: GraphMirror): MenuSection[] {
  return [
    {
      menu: "edit",
      section: "History",
      order: 10,
      entries: () => [
        { label: "Undo", command: "graph.undo", disabled: !graph.state.canUndo },
        { label: "Redo", command: "graph.redo", disabled: !graph.state.canRedo },
      ],
    },
  ];
}

/** The toolbar's undo and redo. */
export function graphToolbar(graph: GraphMirror): ToolbarButton[] {
  return [
    { id: "undo", section: "history", order: 20, icon: "undo", label: "Undo", command: "graph.undo", disabled: () => !graph.state.canUndo },
    { id: "redo", section: "history", order: 20, icon: "redo", label: "Redo", command: "graph.redo", disabled: () => !graph.state.canRedo },
  ];
}
